import { useMemo } from 'react';

import { cn } from '@rocco/utils/cn';
import { ReactComponent as CloseIcon } from '@rocco/icons/svg/close.svg';

import { SelectedItemMedia } from './SelectedItemMedia';
import { countLabel } from './bulk-enquiry-copy';
import type { BulkEnquiryItem } from './bulk-enquiry-state';

interface SelectedItemsListProps {
    items: BulkEnquiryItem[];
    onRemove: (siteTreeID: number) => void;
    /** Renders a clear-all footer. Omit when the consumer places its own
     * clear-all control (the island puts it beside its heading). */
    onClearAll?: () => void;
    /**
     * Group the rows under a heading per supplier. Mirrors how the enquiry is
     * actually sent — one per supplier, carrying only their items — so the
     * fan-out is obvious before submitting.
     */
    groupBySupplier?: boolean;
    className?: string;
}

interface SupplierGroup {
    targetProfessionalId: number;
    supplierName?: string;
    items: BulkEnquiryItem[];
}

/** One selected item: thumbnail, title, a subtitle, and a remove control. */
const ItemRow = ({
    item,
    onRemove,
    showSupplier,
}: {
    item: BulkEnquiryItem;
    onRemove: (siteTreeID: number) => void;
    showSupplier: boolean;
}) => {
    // A professional's title IS their name, so repeating it as the supplier line
    // says nothing — show what they do instead. Products and projects name their
    // supplier, unless a group heading is already saying it.
    const subtitle =
        item.itemType === 'professional'
            ? item.category
            : showSupplier
              ? item.supplierName
              : undefined;

    return (
        <li className="flex items-center gap-x-3 py-2">
            <SelectedItemMedia item={item} className="size-14 rounded-md" />
            <div className="min-w-0 flex-1">
                <p className="line-clamp-1 text-sm font-medium text-[#181818]">
                    {item.title}
                </p>
                {subtitle ? (
                    <p className="line-clamp-1 text-xs text-gray-500">
                        {subtitle}
                    </p>
                ) : null}
            </div>
            <button
                type="button"
                onClick={() => onRemove(item.siteTreeID)}
                aria-label={`Remove ${item.title} from your enquiry`}
                className="relative flex size-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 max-md:before:absolute max-md:before:top-1/2 max-md:before:left-1/2 max-md:before:size-11 max-md:before:-translate-x-1/2 max-md:before:-translate-y-1/2 max-md:before:content-['']"
            >
                <CloseIcon className="size-3 fill-current" />
            </button>
        </li>
    );
};

/**
 * The shared "cart" list of bulk-enquiry selections: a scrollable list of
 * thumbnail + title + supplier rows, each removable, with an optional
 * clear-all footer. Rendered both in the island hover/tap preview (flat) and
 * in the enquiry modal's review step (grouped by supplier). Consumers supply
 * heading + padding.
 */
export const SelectedItemsList = ({
    items,
    onRemove,
    onClearAll,
    groupBySupplier = false,
    className,
}: SelectedItemsListProps) => {
    // Insertion-ordered groups, so the list doesn't reshuffle as items are added.
    const groups = useMemo<SupplierGroup[]>(() => {
        const byProfessional = new Map<number, SupplierGroup>();
        for (const item of items) {
            const existing = byProfessional.get(item.targetProfessionalId);
            if (existing) {
                existing.items.push(item);
                continue;
            }
            byProfessional.set(item.targetProfessionalId, {
                targetProfessionalId: item.targetProfessionalId,
                supplierName: item.supplierName,
                items: [item],
            });
        }
        return [...byProfessional.values()];
    }, [items]);

    if (items.length === 0) {
        return (
            <p
                className={cn(
                    'py-6 text-center text-sm text-gray-500',
                    className,
                )}
            >
                Nothing in your list yet.
            </p>
        );
    }

    return (
        <div className={cn('flex flex-col', className)}>
            <div className="scrollbar-hide max-h-[45vh] overflow-y-auto">
                {groupBySupplier ? (
                    <ul className="divide-y divide-gray-100">
                        {groups.map((group) => (
                            <li
                                key={group.targetProfessionalId}
                                className="py-2"
                            >
                                <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                                    {group.supplierName ?? 'Professional'}
                                    <span className="font-normal normal-case">
                                        {` · ${countLabel(group.items)}`}
                                    </span>
                                </p>
                                <ul>
                                    {group.items.map((item) => (
                                        <ItemRow
                                            key={item.siteTreeID}
                                            item={item}
                                            onRemove={onRemove}
                                            showSupplier={false}
                                        />
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <ul className="divide-y divide-gray-100">
                        {items.map((item) => (
                            <ItemRow
                                key={item.siteTreeID}
                                item={item}
                                onRemove={onRemove}
                                showSupplier
                            />
                        ))}
                    </ul>
                )}
            </div>
            {onClearAll ? (
                <div className="flex justify-end pt-2">
                    <button
                        type="button"
                        onClick={onClearAll}
                        className="rounded px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:text-gray-800 max-md:min-h-11"
                    >
                        Clear all
                    </button>
                </div>
            ) : null}
        </div>
    );
};
